import React, { useState, useEffect } from 'react';
import { 
  Banknote, CreditCard, Calendar, Search, Download, Filter,
  TrendingUp, TrendingDown, DollarSign, RefreshCw, Car, User
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDateTime, formatDate } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentsPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    fetchPayments();
    fetchDrivers();
  }, [activeTab, dateRange, selectedDriver]);

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/drivers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDrivers(data);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get date range
      let startDate, endDate;
      const now = new Date();
      
      switch (dateRange) {
        case 'today':
          startDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
          endDate = new Date().toISOString();
          break;
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7)).toISOString();
          endDate = new Date().toISOString();
          break;
        case 'month':
          startDate = new Date(now.setMonth(now.getMonth() - 1)).toISOString();
          endDate = new Date().toISOString();
          break;
        default:
          startDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
          endDate = new Date().toISOString();
      }

      // Fetch rides with payment info
      let url = `${API_URL}/api/rides?status=completed`;
      if (activeTab !== 'all') {
        url += `&payment_method=${activeTab}`;
      }
      if (selectedDriver !== 'all') {
        url += `&driver_id=${selectedDriver}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const ridesData = await response.json();
        
        // Calculate stats
        const allPayments = ridesData.filter(r => r.status === 'completed');
        const cashPayments = allPayments.filter(r => r.payment_method === 'cash');
        const posPayments = allPayments.filter(r => r.payment_method === 'pos');
        
        setStats({
          totalRevenue: allPayments.reduce((sum, r) => sum + (r.final_price || 0), 0),
          cashRevenue: cashPayments.reduce((sum, r) => sum + (r.final_price || 0), 0),
          posRevenue: posPayments.reduce((sum, r) => sum + (r.final_price || 0), 0),
          totalCount: allPayments.length,
          cashCount: cashPayments.length,
          posCount: posPayments.length
        });

        setPayments(allPayments);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Gabim në ngarkimin e pagesave');
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = payments.filter(p => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        p.passenger_name?.toLowerCase().includes(search) ||
        p.driver_name?.toLowerCase().includes(search) ||
        p.ride_number?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Remove local formatCurrency and formatDate - using imported ones from utils

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pagesat</h1>
          <p className="text-gray-500">Menaxho pagesat cash dhe POS</p>
        </div>
        <button
          onClick={fetchPayments}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Rifresko
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Totali</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.totalRevenue)}</p>
              <p className="text-xs text-gray-400">{stats?.totalCount || 0} transaksione</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Cash</p>
              <p className="text-2xl font-bold text-yellow-600">{formatCurrency(stats?.cashRevenue)}</p>
              <p className="text-xs text-gray-400">{stats?.cashCount || 0} transaksione</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Banknote className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">POS</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(stats?.posRevenue)}</p>
              <p className="text-xs text-gray-400">{stats?.posCount || 0} transaksione</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <div className="flex flex-wrap items-center gap-4">
          {/* Tab Filters */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[
              { id: 'all', label: 'Të gjitha', icon: DollarSign },
              { id: 'cash', label: 'Cash', icon: Banknote },
              { id: 'pos', label: 'POS', icon: CreditCard },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Date Range */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="today">Sot</option>
            <option value="week">7 ditët e fundit</option>
            <option value="month">30 ditët e fundit</option>
          </select>

          {/* Driver Filter */}
          <select
            value={selectedDriver}
            onChange={(e) => setSelectedDriver(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="all">Të gjithë shoferët</option>
            {drivers.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>

          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Kërko..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nr.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pasagjeri</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shoferi</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rruga</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metoda</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Shuma</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <RefreshCw className="w-6 h-6 mx-auto mb-2 text-gray-300 animate-spin" />
                    <p className="text-sm text-gray-500">Duke ngarkuar...</p>
                  </td>
                </tr>
              ) : filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <DollarSign className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                    <p className="text-sm text-gray-500">Nuk ka pagesa</p>
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                        {payment.ride_number || `#${payment.id?.slice(-6).toUpperCase()}`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(payment.completed_at || payment.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <span className="text-sm font-medium">{payment.passenger_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                          <Car className="w-4 h-4 text-yellow-600" />
                        </div>
                        <span className="text-sm">{payment.driver_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">
                      {payment.pickup_address?.split(',')[0]} → {payment.dropoff_address?.split(',')[0]}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        payment.payment_method === 'cash'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {payment.payment_method === 'cash' ? (
                          <><Banknote className="w-3 h-3" /> Cash</>
                        ) : (
                          <><CreditCard className="w-3 h-3" /> POS</>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(payment.final_price)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentsPage;
